import React, { useEffect, useState } from "react"
import Skeleton from "react-loading-skeleton"
import "react-loading-skeleton/dist/skeleton.css"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { Container, Row, Col, Input, Button, Label, Card, CardBody, CardHeader } from "reactstrap"
import { toast } from "react-toastify"
import axios from "axios"

const AppVersionSettings = () => {
  const [userAppVersion, setUserAppVersion] = useState("")
  const [driverAppVersion, setDriverAppVersion] = useState("")
  const [userAppBeta, setUserAppBeta] = useState(false)
  const [driverAppBeta, setDriverAppBeta] = useState(false)
  const [allowNegOneBooking, setAllowNegOneBooking] = useState(false)
  const [userAppAndroidUrl, setUserAppAndroidUrl] = useState("")
  const [userAppIosUrl, setUserAppIosUrl] = useState("")
  const [driverAppAndroidUrl, setDriverAppAndroidUrl] = useState("")
  const [driverAppIosUrl, setDriverAppIosUrl] = useState("")
  const [loading, setLoading] = useState(true)

  const handleSaveUser = async () => {
    try {
      const token = localStorage.getItem("authToken")
      const body = {
        user_app_version: userAppVersion,
        user_app_beta: userAppBeta ? 1 : 0,
        user_app_android_url: userAppAndroidUrl,
        user_app_ios_url: userAppIosUrl,
      }
      const response = await axios.post(
        `${process.env.REACT_APP_WEB_APP_URL}/api/web/app-versions`,
        body,
        {
          method: "POST",
          headers: {
            "Content-type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      )
      if (response.status == 200) {
        toast.success("User app settings updated successfully", {
          autoClose: 3000,
        })
      } else {
        toast.error(response?.message || "Failed to update user app settings", {
          autoClose: 3000,
        })
      }
    } catch (error) {
      console.error(error)
      toast.error("Failed to update user app settings", { autoClose: 3000 })
    }
  }

  const handleSaveDriver = async () => {
    try {
      const token = localStorage.getItem("authToken")
      const body = {
        driver_app_version: driverAppVersion,
        driver_app_beta: driverAppBeta ? 1 : 0,
        driver_app_android_url: driverAppAndroidUrl,
        driver_app_ios_url: driverAppIosUrl,
        allow_driver_negative_wallet_one_booking: allowNegOneBooking ? 1 : 0,
      }
      const response = await axios.post(
        `${process.env.REACT_APP_WEB_APP_URL}/api/web/app-versions`,
        body,
        {
          method: "POST",
          headers: {
            "Content-type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      )
      if (response.status == 200) {
        toast.success("Driver app settings updated successfully", {
          autoClose: 3000,
        })
      } else {
        toast.error(response?.message || "Failed to update driver app settings", {
          autoClose: 3000,
        })
      }
    } catch (error) {
      console.error(error)
      toast.error("Failed to update driver app settings", { autoClose: 3000 })
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("authToken")
        const response = await axios.get(
          `${process.env.REACT_APP_WEB_APP_URL}/api/web/app-versions`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        if (response.status === 200) {
          const { 
            user_app_version, 
            driver_app_version, 
            user_app_beta, 
            driver_app_beta,
            user_app_android_url,
            user_app_ios_url,
            driver_app_android_url,
            driver_app_ios_url,
            allow_driver_negative_wallet_one_booking
          } = response.data.data
          setUserAppVersion(user_app_version || "")
          setDriverAppVersion(driver_app_version || "")
          setUserAppBeta(user_app_beta === 1 || user_app_beta === true)
          setDriverAppBeta(driver_app_beta === 1 || driver_app_beta === true)
          setUserAppAndroidUrl(user_app_android_url || "")
          setUserAppIosUrl(user_app_ios_url || "")
          setDriverAppAndroidUrl(driver_app_android_url || "")
          setDriverAppIosUrl(driver_app_ios_url || "")
          setAllowNegOneBooking(
            allow_driver_negative_wallet_one_booking === 1 ||
              allow_driver_negative_wallet_one_booking === true
          )
        }
      } catch (error) {
        console.error(error)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  document.title = "App Version Settings | Moeen"
  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Home" breadcrumbItem="App Version Settings" />
        {loading ? (
          <Skeleton count={10} height={40} />
        ) : (
          <div className="page-content">
            <Container fluid>
              <Row className="mb-4">
                <Col lg={12}>
                  <h4 className="mb-2">Application Versions</h4>
                  <p className="text-muted">
                    Configure the minimum required versions for User and Driver applications.
                    Android and iOS applications will automatically check their version and display 
                    a notification if an update is required.
                  </p>
                </Col>
              </Row>

              {/* User App Card */}
              <Row className="mb-4">
                <Col lg={12}>
                  <Card>
                    <CardHeader className="bg-primary text-white">
                      <h5 className="card-title mb-0">
                        <i className="mdi mdi-account me-2"></i>
                        User Application Settings
                      </h5>
                    </CardHeader>
                    <CardBody>
                      <Row className="mb-3">
                        <Col md={12}>
                          <Label className="form-label">Version (minimum required)</Label>
                          <Input
                            type="text"
                            placeholder="Ex: 1.0.0"
                            value={userAppVersion}
                            onChange={(e) => setUserAppVersion(e.target.value)}
                          />
                          <div className="form-text">
                            Format: X.Y.Z (ex: 1.2.3). Users with a lower version will see a notification.
                          </div>
                        </Col>
                      </Row>

                      <Row className="mb-3">
                        <Col md={12}>
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <Label className="form-label mb-0">Beta Version</Label>
                              <div className="form-text">
                                Mark if the User application is in beta version
                              </div>
                            </div>
                            <div className="form-check form-switch form-switch-lg">
                              <Input
                                type="checkbox"
                                className="form-check-input"
                                id="userAppBeta"
                                checked={userAppBeta}
                                onChange={(e) => setUserAppBeta(e.target.checked)}
                                style={{ width: "50px", height: "25px", cursor: "pointer" }}
                              />
                            </div>
                          </div>
                        </Col>
                      </Row>

                      <Row className="mb-3">
                        <Col md={12}>
                          <Label className="form-label">Android Update URL</Label>
                          <Input
                            type="url"
                            placeholder="https://play.google.com/store/apps/details?id=com.example.user"
                            value={userAppAndroidUrl}
                            onChange={(e) => setUserAppAndroidUrl(e.target.value)}
                          />
                          <div className="form-text">
                            Google Play Store URL for User app Android updates
                          </div>
                        </Col>
                      </Row>

                      <Row className="mb-3">
                        <Col md={12}>
                          <Label className="form-label">iOS Update URL</Label>
                          <Input
                            type="url"
                            placeholder="https://apps.apple.com/app/id123456789"
                            value={userAppIosUrl}
                            onChange={(e) => setUserAppIosUrl(e.target.value)}
                          />
                          <div className="form-text">
                            App Store URL for User app iOS updates
                          </div>
                        </Col>
                      </Row>

                      <div className="text-end mt-4">
                        <Button
                          onClick={handleSaveUser}
                          color="primary"
                          className="px-4"
                        >
                          <i className="mdi mdi-content-save me-2"></i>
                          Save User App Settings
                        </Button>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              {/* Driver App Card */}
              <Row>
                <Col lg={12}>
                  <Card>
                    <CardHeader className="bg-success text-white">
                      <h5 className="card-title mb-0">
                        <i className="mdi mdi-car me-2"></i>
                        Driver Application Settings
                      </h5>
                    </CardHeader>
                    <CardBody>
                      <Row className="mb-3">
                        <Col md={12}>
                          <Label className="form-label">Version (minimum required)</Label>
                          <Input
                            type="text"
                            placeholder="Ex: 1.0.0"
                            value={driverAppVersion}
                            onChange={(e) => setDriverAppVersion(e.target.value)}
                          />
                          <div className="form-text">
                            Format: X.Y.Z (ex: 1.2.3). Drivers with a lower version will see a notification.
                          </div>
                        </Col>
                      </Row>

                      <Row className="mb-3">
                        <Col md={12}>
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <Label className="form-label mb-0">Beta Version</Label>
                              <div className="form-text">
                                Mark if the Driver application is in beta version
                              </div>
                            </div>
                            <div className="form-check form-switch form-switch-lg">
                              <Input
                                type="checkbox"
                                className="form-check-input"
                                id="driverAppBeta"
                                checked={driverAppBeta}
                                onChange={(e) => setDriverAppBeta(e.target.checked)}
                                style={{ width: "50px", height: "25px", cursor: "pointer" }}
                              />
                            </div>
                          </div>
                        </Col>
                      </Row>

                      <Row className="mb-3">
                        <Col md={12}>
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <Label className="form-label mb-0">
                                Allow 1 booking if wallet is insufficient
                              </Label>
                              <div className="form-text">
                                If enabled: driver with wallet ≥ 0 can accept one booking (wallet may become negative). If wallet is already negative, driver is blocked.
                              </div>
                            </div>
                            <div className="form-check form-switch form-switch-lg">
                              <Input
                                type="checkbox"
                                className="form-check-input"
                                id="allowNegOneBooking"
                                checked={allowNegOneBooking}
                                onChange={(e) => setAllowNegOneBooking(e.target.checked)}
                                style={{ width: "50px", height: "25px", cursor: "pointer" }}
                              />
                            </div>
                          </div>
                        </Col>
                      </Row>

                      <Row className="mb-3">
                        <Col md={12}>
                          <Label className="form-label">Android Update URL</Label>
                          <Input
                            type="url"
                            placeholder="https://play.google.com/store/apps/details?id=com.example.driver"
                            value={driverAppAndroidUrl}
                            onChange={(e) => setDriverAppAndroidUrl(e.target.value)}
                          />
                          <div className="form-text">
                            Google Play Store URL for Driver app Android updates
                          </div>
                        </Col>
                      </Row>

                      <Row className="mb-3">
                        <Col md={12}>
                          <Label className="form-label">iOS Update URL</Label>
                          <Input
                            type="url"
                            placeholder="https://apps.apple.com/app/id987654321"
                            value={driverAppIosUrl}
                            onChange={(e) => setDriverAppIosUrl(e.target.value)}
                          />
                          <div className="form-text">
                            App Store URL for Driver app iOS updates
                          </div>
                        </Col>
                      </Row>

                      <div className="text-end mt-4">
                        <Button
                          onClick={handleSaveDriver}
                          color="success"
                          className="px-4"
                        >
                          <i className="mdi mdi-content-save me-2"></i>
                          Save Driver App Settings
                        </Button>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </Container>
          </div>
        )}
      </div>
    </div>
  )
}

export default AppVersionSettings

